import { create } from "zustand";
import { ArtistService, type ContactDetailsData } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";

interface ContactDetailsState {
  data: ContactDetailsData | null;
  isLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: ContactDetailsData | null) => void;
  fetchContactDetails: () => Promise<ContactDetailsData | null>;
  /** Fetches only if !hasFetchedInSession. Call when user opens the section. */
  ensureFetched: () => Promise<ContactDetailsData | null>;
  /** Call on logout to reset session cache. */
  reset: () => void;
}

let inFlightFetch: Promise<ContactDetailsData | null> | null = null;

const initialState: Omit<ContactDetailsState, "setData" | "fetchContactDetails" | "ensureFetched" | "reset"> = {
  data: null,
  isLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useContactDetailsStore = create<ContactDetailsState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchContactDetails: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getContactDetails();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({
          error: "Failed to load contact details",
          isLoading: false,
          data: null,
        });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchContactDetails, data } = get();
    if (hasFetchedInSession) return data;
    return fetchContactDetails();
  },

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));

